import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FieldHelperProps, FieldInputProps, FieldMetaProps } from 'formik';
import { DefaultOptionType } from 'antd/lib/select';
import Notification from 'components/ui/base/notification/Notification';
import { listTeams } from 'store/teams/client';
import { GetTeamsParams, Team, TeamStatuses } from 'store/teams/types';
import FormAutocomplete from 'components/new-ui/composed/formAutocomplete/FormAutocomplete';
import { SizeType } from 'antd/lib/config-provider/SizeContext';

const defaultListTeamsParams: GetTeamsParams = {
    page: 0,
    size: 100,
    status: TeamStatuses.ACTIVE,
};

interface TeamSelectFieldProps {
    fieldProps: FieldInputProps<any>;
    fieldMeta: FieldMetaProps<any>;
    fieldHelper: FieldHelperProps<any>;
    size?: SizeType;
}

const TeamSelectField = ({ fieldProps, fieldMeta, fieldHelper, size }: TeamSelectFieldProps) => {
    const [teams, setTeams] = useState<Team[]>([]);
    const [teamInput, setTeamInput] = useState('');

    const fetchTeams = useCallback(async (query?: string) => {
        try {
            const params: GetTeamsParams = {
                ...defaultListTeamsParams,
                page: 0,
                ...(query?.trim() ? { query: query.trim() } : {}),
            };
            const res = await listTeams(params);
            setTeams(res.list ?? []);
        } catch (err) {
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Error',
                description: 'Failed to load teams',
            });
            setTeams([]);
        }
    }, []);

    const debouncedFetchTeams = useMemo(() => debounce((q: string) => fetchTeams(q), 300), [fetchTeams]);

    const teamsOptions: DefaultOptionType[] = useMemo(() => {
        if (!teams) return [];

        const teamsMap = new Map(teams.map((t) => [t.id, t]));
        const uniqueTeams = Array.from(teamsMap.values());

        return uniqueTeams.map((t) => ({
            label: t.name,
            value: t.id,
        }));
    }, [teams]);

    const handleSelectTeam = (val: string, option?: DefaultOptionType) => {
        setTeamInput(String(option?.label ?? ''));
        fieldHelper?.setValue(val);
        debouncedFetchTeams.cancel();
    };

    const handleClearTeam = () => {
        setTeamInput('');
        fieldHelper.setValue(undefined);
    };

    const handleSearchTeams = (val: string) => {
        setTeamInput(val);
        debouncedFetchTeams(val);
    };

    useEffect(() => {
        fetchTeams();

        return () => debouncedFetchTeams.cancel();
    }, [fetchTeams, debouncedFetchTeams]);

    // Populate the visible label when a team is already selected (e.g. editing an existing record)
    useEffect(() => {
        if (fieldProps.value && !teamInput) {
            const selectedTeam = teams.find((t) => t.id === fieldProps.value);
            if (selectedTeam) {
                setTeamInput(selectedTeam.name);
            }
        }
    }, [fieldProps.value, teamInput, teams]);

    return (
        <FormAutocomplete
            size={size}
            required
            fieldName={fieldProps.name}
            inputPlaceholder="Select a team"
            style={{ width: '100%', marginTop: 6 }}
            options={teamsOptions}
            fieldMeta={fieldMeta}
            onSelect={handleSelectTeam}
            onSearch={handleSearchTeams}
            onClear={handleClearTeam}
            onBlur={() => fieldHelper.setTouched(true)}
            value={teamInput}
        />
    );
};

export default TeamSelectField;
